import Routes from './routes'
import ThemeConfig from './theme'
import { useEffect, useState } from 'react'
import { SnackbarProvider } from 'notistack'
import NavigationScroll from './layout/NavigationScroll'
import CircularProgressScreen from './components/CircularProgress'
import { useNavigate, useLocation } from 'react-router-dom'
import axios from 'axios'
import { useDispatch } from 'react-redux'
import { getProfile } from './features/auth/redux/authAction'

// Shopify App Bridge
import { getSessionToken } from '@shopify/app-bridge/utilities'
import createApp from '@shopify/app-bridge'
import { AppProvider as PolarisProvider } from '@shopify/polaris'
import '@shopify/polaris/build/esm/styles.css'

const App = () => {
  const dispatch = useDispatch()
  const [userInfo, setUserInfo] = useState(null)
  const [isLogged, setIsLogged] = useState(null)
  const [loading, setLoading] = useState(true)
  const [shopifyConfig, setShopifyConfig] = useState(null)

  const navigate = useNavigate()
  const location = useLocation()

  // ❌ REMOVED — this frame-busting code breaks Shopify embedding!
  // useEffect(() => {
  //   if (window.top !== window.self) {
  //     window.top.location = window.self.location;
  //   }
  // }, []);

  // ✅ Initialize Shopify App Bridge config from URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const host = params.get('host')
    const shop = params.get('shop')

    if (host) {
      setShopifyConfig({
        apiKey: process.env.REACT_APP_SHOPIFY_API_KEY,
        host: host,
        forceRedirect: true,
      })
      // Store shop for later use
      if (shop) {
        sessionStorage.setItem('shopify_shop', shop)
      }
    }
  }, [location.search])

  // ✅ Set up axios interceptor to attach Shopify session token to every request
  useEffect(() => {
    if (!shopifyConfig) return

    const app = createApp(shopifyConfig)

    const interceptor = axios.interceptors.request.use(async config => {
      try {
        const token = await getSessionToken(app)
        config.headers.Authorization = `Bearer ${token}`
      } catch (e) {
        console.error('Failed to get Shopify session token', e)
      }
      return config
    })

    return () => {
      axios.interceptors.request.eject(interceptor)
    }
  }, [shopifyConfig])

  // ✅ Updated auth flow
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const authCode = urlParams.get('code')
    const shop = urlParams.get('shop')
    const host = urlParams.get('host')

    // Don't run auth until we have Shopify config OR there's no host (non-embedded mode)
    if (host && !shopifyConfig) return

    if (authCode) {
      // OAuth callback from Shopify
      axios
        .post(
          `${process.env.REACT_APP_SCHEMA_URL}/admin/auth/callback`,
          { code: authCode, shop, host },
          { withCredentials: true }
        )
        .then(() => dispatch(getProfile()))
        .then(res => {
          setUserInfo(res.data)
          setIsLogged(true)
        })
        .catch(error => {
          console.error('Login failed or profile error', error)
          setIsLogged(false)
        })
        .finally(() => {
          // Preserve host and shop params during navigation
          const cleanParams = new URLSearchParams()
          if (shop) cleanParams.set('shop', shop)
          if (host) cleanParams.set('host', host)
          navigate(`/?${cleanParams.toString()}`, { replace: true })
          setLoading(false)
        })
    } else {
      dispatch(getProfile())
        .then(res => {
          setUserInfo(res.data)
          setIsLogged(true)
        })
        .catch(() => {
          setIsLogged(false)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [shopifyConfig])

  if (loading || isLogged === null) {
    return <CircularProgressScreen />
  }

  const appContent = (
    <ThemeConfig>
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <NavigationScroll>
          <Routes isLogged={isLogged} userInfo={userInfo} />
        </NavigationScroll>
      </SnackbarProvider>
    </ThemeConfig>
  )

  // If embedded in Shopify (host param exists), wrap with App Bridge + Polaris
  if (shopifyConfig) {
    return (
      <PolarisProvider i18n={{}}>
        {appContent}
      </PolarisProvider>
    )
  }

  // Fallback for non-Shopify standalone usage
  return appContent
}

export default App
